import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Fade,
  FormControlLabel,
  Switch,
  Card,
  CardMedia,
  Grid,
  Slider
} from '@mui/material';
import {
  ArrowBack,
  DirectionsRun,
  CameraAlt
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';

// Import the dummy data
import runningData from '../data/test.json';

// Styled Components for Gallery
const GalleryCard = styled(Card)(({ theme }) => ({
  // A4 aspect ratio: 1:1.414 (width:height)
  aspectRatio: '1 / 1.414',
  maxWidth: '280px',
  margin: '0 auto',
  background: '#2a2a2a',
  borderRadius: '12px',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.4)',
  },
  '&:hover .gallery-overlay': {
    opacity: 1,
  }
}));

const GalleryOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.8) 50%, transparent 100%)',
  padding: theme.spacing(3),
  color: 'white',
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#f0f0f0',
  fontWeight: '600',
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    background: '#66bb6a',
    borderRadius: '2px',
  }
}));

// Helper function to get event image from the event data
const getEventImage = (event: any): string | undefined => {
  return event.image || undefined;
};

// Function to format event type for display
const formatEventType = (eventType: string): string => {
  const typeMap: { [key: string]: string } = {
    'half-marathon': 'Half Marathon',
    '10k': '10K Race',
    '5k': '5K Race', 
    'other': 'Special Event',
    'marathon': 'Marathon',
    'trail': 'Trail Run'
  };
  return typeMap[eventType] || 'Running Event';
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  ScatterController
);

interface WeeklyData {
  week: string;
  weekLabel: string;
  kilometers: number;
}


const RunningPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [chartData, setChartData] = useState<any>(null);
  const [isMonthlyView, setIsMonthlyView] = useState(false);
  
  // Date range state for filtering
  const allWeeklyData = runningData.runningData.weeklyKilometers;
  const allDates = allWeeklyData.map(week => new Date(week.week).getTime()).sort((a, b) => a - b);
  const minDate = allDates[0];
  const maxDate = allDates[allDates.length - 1];
  const [dateRange, setDateRange] = useState<number[]>([minDate, maxDate]);
  const [filteredEvents, setFilteredEvents] = useState(runningData.runningData.events);
  const [filteredWeeklyData, setFilteredWeeklyData] = useState(runningData.runningData.weeklyKilometers);

  useEffect(() => {
    // Process the running data for Chart.js
    const allWeeklyData: WeeklyData[] = runningData.runningData.weeklyKilometers;
    const allEvents = runningData.runningData.events;
    
    // Filter data based on selected date range
    const weeklyData = allWeeklyData.filter(week => {
      const weekDate = new Date(week.week).getTime();
      return weekDate >= dateRange[0] && weekDate <= dateRange[1];
    });
    
    const events = allEvents.filter(event => {
      const eventDate = new Date(event.week).getTime();
      return eventDate >= dateRange[0] && eventDate <= dateRange[1];
    });
    
    // Function to aggregate weekly data into monthly data
    const aggregateToMonthly = (weeklyData: WeeklyData[]) => {
      const monthlyMap = new Map();
      
      weeklyData.forEach(week => {
        const date = new Date(week.week);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        
        if (monthlyMap.has(monthKey)) {
          monthlyMap.get(monthKey).kilometers += week.kilometers;
        } else {
          monthlyMap.set(monthKey, {
            month: monthKey,
            monthLabel: monthLabel,
            kilometers: week.kilometers
          });
        }
      });
      
      return Array.from(monthlyMap.values()).sort((a, b) => a.month.localeCompare(b.month));
    };


    // Determine which data to use based on view toggle
    const displayData = isMonthlyView ? aggregateToMonthly(weeklyData) : weeklyData;
    const maxKilometers = Math.max(...displayData.map(item => item.kilometers));
    const dotYPosition = maxKilometers + (maxKilometers * 0.1);

    // Create event markers based on view
    let eventMarkers;
    if (isMonthlyView) {
      // Monthly view - map events directly to their month labels
      eventMarkers = events.map((event) => {
        const eventDate = new Date(event.week);
        const eventMonthLabel = eventDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        
        return {
          x: eventMonthLabel, // Use the month label that matches the monthly data
          y: dotYPosition,
          event: event
        };
      }).filter(Boolean);
    } else {
      // Weekly view - existing logic
      eventMarkers = events.map((event) => {
        const matchingWeek = weeklyData.find(week => week.week === event.week);
        
        if (matchingWeek) {
          return {
            x: matchingWeek.weekLabel,
            y: dotYPosition,
            event: event
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Get colors for different event types
    const getEventColor = (eventType: string) => {
      switch (eventType) {
        case 'marathon': return '#f44336'; // Red
        case 'half-marathon': return '#ff9800'; // Orange  
        case '10k': return '#2196f3'; // Blue
        case 'trail': return '#4caf50'; // Green
        case '5k': return '#9c27b0'; // Purple
        case 'other': return '#9e9e9e'; // Brown
        default: return '#607d8b'; // Blue Grey
      }
    };

    const data = {
      labels: displayData.map(item => isMonthlyView ? item.monthLabel : item.weekLabel),
      datasets: [
        {
          type: 'bar' as const,
          label: isMonthlyView ? 'Kilometers Run (Monthly)' : 'Kilometers Run (Weekly)',
          data: displayData.map(item => item.kilometers),
          backgroundColor: 'rgba(102, 187, 106, 0.8)',
          borderColor: 'rgba(102, 187, 106, 1)',
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
          hoverBackgroundColor: 'rgba(102, 187, 106, 0.9)',
          hoverBorderColor: 'rgba(102, 187, 106, 1)',
          order: 2,
        },
        {
          type: 'scatter' as const,
          label: 'Events',
          data: eventMarkers,
          backgroundColor: eventMarkers.map((marker: any) => 
            getEventColor(marker.event.eventType)
          ),
          borderColor: eventMarkers.map((marker: any) => 
            getEventColor(marker.event.eventType)
          ),
          borderWidth: 3,
          pointRadius: 10,
          pointHoverRadius: 14,
          order: 1,
          showLine: false,
          // Use circles for all event types
          pointStyle: 'circle',
        },
      ],
    };

    setChartData(data);
    setFilteredEvents(events);
    setFilteredWeeklyData(weeklyData);
    setFilteredEvents(events);
  }, [isMobile, isMonthlyView, dateRange]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true, // Re-enable built-in tooltips
        backgroundColor: 'rgba(26, 26, 26, 0.95)',
        titleColor: 'white',
        bodyColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(102, 187, 106, 1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        displayColors: false,
        callbacks: {
          title: function(context: any) {
            if (context[0]?.datasetIndex === 1) {
              // Event marker - show event name as title
              const eventData = (context[0].raw as any).event;
              return `🏃 ${eventData.name}`;
            }
            // Bar chart - show week/month label
            return context[0]?.label || '';
          },
          label: function(context: any) {
            if (context.datasetIndex === 0) {
              // Bar chart - show distance
              const label = isMonthlyView ? 'Monthly Distance' : 'Weekly Distance';
              return `📏 ${label}: ${context.parsed.y} km`;
            } else {
              // Event markers - only show event-specific data
              const eventData = (context.raw as any).event;
              
              return [
                `📅 ${eventData.date}`,
                `📍 ${eventData.location}`,
                `🏁 ${eventData.distance}km`,
                `⏱️ ${eventData.time}`,
                `📈 ${eventData.pace}`,
              ].filter(Boolean);
            }
          },
          afterBody: function(context: any) {
            if (context[0]?.datasetIndex === 1) {
              // Event markers - show notes if available
              const eventData = (context[0].raw as any).event;
              return eventData.notes ? `\n📝 ${eventData.notes}` : '';
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: isMobile ? 9 : 11, // Slightly smaller font for many labels
          },
          maxRotation: 45,
          minRotation: 45,
          // Show labels more selectively to reduce clutter with many bars
          callback: function(value: any, index: any, ticks: any) {
            // Show every 8th week for desktop, every 12th for mobile to reduce overcrowding
            if (index % (isMobile ? 12 : 8) === 0) {
              return chartData?.labels[index] || '';
            }
            return '';
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: isMobile ? 10 : 12,
          },
          callback: function(value: any) {
            return value + ' km';
          },
        },
      },
    },
    interaction: {
      intersect: true, // Only show tooltip for the exact element being hovered
      mode: 'point' as const, // Only trigger on the specific point, not the entire index
    },
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
      paddingTop: '64px'
    }}>
      {/* Navigation Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          background: 'rgba(26, 26, 26, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2, color: '#e0e0e0', '&:hover': { color: 'white' } }}
          >
            <ArrowBack />
          </IconButton>
          <DirectionsRun sx={{ mr: 2, color: '#66bb6a' }} />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: 'white',
              fontWeight: 600 
            }}
          >
            Running Habits
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Fade in={true} timeout={800}>
          <Box>
            {/* Page Header */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  color: 'white',
                  textAlign: 'center',
                  mb: 2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                {isMonthlyView ? 'Monthly' : 'Weekly'} Running Distance
              </Typography>
              <Typography 
                variant="h6" 
                component="p" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)',
                  textAlign: 'center',
                  mb: 4
                }}
              >
                Track your running progress over time
              </Typography>
            </Box>

            {/* Chart Container */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 3,
                backgroundColor: 'rgba(42, 42, 42, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                overflow: 'hidden'
              }}
            >
              {/* Chart Controls */}
              <Box sx={{ mb: 3 }}>
                {/* View Toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isMonthlyView}
                        onChange={(e) => setIsMonthlyView(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#66bb6a',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#66bb6a',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                        Monthly View
                      </Typography>
                    }
                    sx={{ color: 'white' }}
                  />
                </Box>

                {/* Date Range Slider */}
                <Box sx={{ px: 2 }}>
                  <Typography 
                    variant="subtitle1" 
                    component="h3" 
                    gutterBottom
                    sx={{ 
                      color: 'white',
                      textAlign: 'center',
                      mb: 2,
                      fontWeight: 500
                    }}
                  >
                    Date Range Filter
                  </Typography>
                  <Slider
                    value={dateRange}
                    onChange={(event, newValue) => setDateRange(newValue as number[])}
                    valueLabelDisplay="auto"
                    min={minDate}
                    max={maxDate}
                    valueLabelFormat={(value) => new Date(value).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short'
                    })}
                    sx={{
                      color: '#66bb6a',
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#66bb6a',
                        border: '2px solid #ffffff',
                        '&:hover': {
                          boxShadow: '0px 0px 0px 8px rgba(102, 187, 106, 0.16)',
                        },
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: '#66bb6a',
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiSlider-valueLabel': {
                        backgroundColor: '#66bb6a',
                        color: 'white',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {new Date(dateRange[0]).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {new Date(dateRange[1]).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box 
                sx={{ 
                  height: '500px',
                  width: '100%',
                  // Remove overflow settings to disable scrolling
                }}
              >
                <Box 
                  sx={{ 
                    height: '460px',
                    width: '100%', // Use full width instead of calculated minimum
                    position: 'relative'
                  }}
                >
                  {chartData && (
                    <Chart 
                      type="bar"
                      data={chartData} 
                      options={chartOptions}
                    />
                  )}
                </Box>
              </Box>
              
              {/* Updated hint for dots visualization */}
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                  mt: 2,
                  fontStyle: 'italic'
                }}
              >
                Colored stars indicate events • Hover for event details • All {chartData?.labels?.length || 0} {isMonthlyView ? 'months' : 'weeks'} displayed
              </Typography>
                            <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 2
              }}>
                {[
                    { type: '5k', color: '#9c27b0', label: '5K' },
                    { type: '10k', color: '#2196f3', label: '10K' },
                    { type: 'half-marathon', color: '#ff9800', label: 'Half Marathon' },
                    { type: 'marathon', color: '#f44336', label: 'Marathon' },
                    { type: 'other', color: '#9e9e9e', label: 'Trail Run' },
                ].map((eventType) => (
                  <Box 
                    key={eventType.type}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      padding: '8px 12px',
                      borderRadius: '16px',
                      border: `1px solid ${eventType.color}30`
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: eventType.color,
                        border: `2px solid ${eventType.color}`
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}
                    >
                      {eventType.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Running Gallery */}
            <Box sx={{ mt: 6 }}>
              <SectionTitle variant="h4" textAlign="center" sx={{ mb: 4 }}>
                <CameraAlt sx={{ mr: 2, verticalAlign: 'middle' }} />
                Running Gallery
              </SectionTitle>
              
              <Grid container spacing={4} justifyContent="center">
                {filteredEvents
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((event, index) => (
                  <Grid item xs={12} sm={6} md={4} key={event.id}>
                    <Fade in={true} timeout={800 + index * 200}>
                      <GalleryCard
                        onClick={() => {
                          // You can add modal functionality here or link to full-size image
                          console.log('Event clicked:', event.name);
                        }}
                      >
                        {getEventImage(event) ? (
                          <CardMedia
                            component="img"
                            height="100%"
                            image={getEventImage(event)}
                            alt={event.name}
                            sx={{ 
                              objectFit: 'cover',
                              height: '100%',
                              filter: 'brightness(0.9)'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: '100%',
                              backgroundColor: '#2a2a2a',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)',
                            }}
                          >
                            <Box sx={{ textAlign: 'center', color: '#66bb6a', opacity: 0.6 }}>
                              <DirectionsRun sx={{ fontSize: 60, mb: 2 }} />
                              <Typography variant="h6" sx={{ fontWeight: '600' }}>
                                {formatEventType(event.eventType)}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <GalleryOverlay className="gallery-overlay">
                          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2 }}>
                            {event.name}
                          </Typography>
                          
                          {/* Event Properties Grid */}
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr',
                            gap: 1.5,
                            mb: 2
                          }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#66bb6a', fontWeight: '600' }}>
                                Distance
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {event.distance} km
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#66bb6a', fontWeight: '600' }}>
                                Time
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {event.time}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#66bb6a', fontWeight: '600' }}>
                                Pace
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {event.pace}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#66bb6a', fontWeight: '600' }}>
                                Date
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {new Date(event.date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.85rem', mb: 1 }}>
                            📍 {event.location}
                          </Typography>
                          
                          {event.notes && (
                            <Typography variant="body2" sx={{ 
                              opacity: 0.7, 
                              fontSize: '0.8rem',
                              fontStyle: 'italic',
                              borderLeft: '2px solid #66bb6a',
                              paddingLeft: 1,
                              mt: 1
                            }}>
                              "{event.notes}"
                            </Typography>
                          )}
                        </GalleryOverlay>
                        
                        {/* Always visible event type badge */}
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 12, 
                          right: 12, 
                          background: 'rgba(0, 0, 0, 0.7)',
                          px: 2,
                          py: 0.5,
                          borderRadius: '12px',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <Typography variant="caption" sx={{ 
                            color: '#66bb6a', 
                            fontWeight: '600',
                            fontSize: '0.75rem'
                          }}>
                            {formatEventType(event.eventType)}
                          </Typography>
                        </Box>
                      </GalleryCard>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontStyle: 'italic'
                }}>
                  Hover to view event details • Click for more info • {filteredEvents.length} running events captured
                </Typography>
              </Box>
            </Box>

            {/* Stats Summary */}
            <Box sx={{ mt: 4 }}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3,
                  backgroundColor: 'rgba(42, 42, 42, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'white',
                    mb: 3
                  }}
                >
                  Quick Stats
                </Typography>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: 3
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#66bb6a' }}>
                      {filteredWeeklyData.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Weeks Tracked
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#42a5f5' }}>
                      {filteredWeeklyData
                        .reduce((sum, week) => sum + week.kilometers, 0)
                        .toFixed(1)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Total Kilometers
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff7043' }}>
                      {filteredWeeklyData.length > 0 ? (filteredWeeklyData
                        .reduce((sum, week) => sum + week.kilometers, 0) / 
                        filteredWeeklyData.length)
                        .toFixed(1) : '0.0'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Average per Week
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default RunningPage;